import { Button, IconButton, Paper } from "@mui/material";
import React from "react";
import styled from "styled-components/macro";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Trans, useTranslation } from "react-i18next";
import { useViewerQuery } from "../../queries/viewerQuery";
import { useNavigate } from "react-router";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import { graphql } from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";
import { VerifyMutation } from "./__generated__/VerifyMutation.graphql";
import { VerifyingMutation } from "./__generated__/VerifyingMutation.graphql";
import { useSeeHint } from "../../components/hint/Hint";
import { devicePaddings, isApp } from "../../tools/device";
import { useSearchParams } from "react-router-dom";
import { VERIFY } from "../RootRouter.config";
import { USEREDIT } from "../profile/ProfileRouter.config";
import { snackbar } from "../../tools/events";
import DownloadButton from "root/auth/DownloadButton";
import { useIdle } from "react-use";
import { useGetState } from "../../components/ReduxProvider";

export const VERIFYHINT = "verifyHint";
export const VERIFYTRANS = "Verify your email";

const { top } = devicePaddings();

const ResendQL = graphql`
  mutation VerifyMutation {
    resendVerification
  }
`;

const VerifyUserQL = graphql`
  mutation VerifyingMutation($hash: String!) {
    verifyUser(hash: $hash) {
      emailVerified
      id
      email
      subscriptions {
        transactionId
        storeId
        timeToExpire
        platform
        family
        cancelled
      }
    }
  }
`;

const Wrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: ${({ theme }) => theme.duo.color.lightGrey};
`;
const SIconButton = styled(IconButton)`
  position: absolute;
  top: ${top + 5}px;
  right: 5px;
  svg {
    color: #ccc;
  }
`;
const SPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  width: 85%;
  padding: 50px 15px;
`;
const TopIco = styled.div`
  margin-bottom: 10px;
  svg {
    color: ${({ theme }) => theme.duo.color.primary};
    font-size: 40px;
  }
`;
const Title = styled.div`
  font-size: ${({ theme }) => theme.duo.fontSize.medium};
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  margin: 20px 0;
`;
const Text = styled.div`
  margin-bottom: 20px;
  font-size: 95%;
`;
const Progress = styled.div`
  margin: 40px;
`;
const Green = styled.p`
  color: ${({ theme }) => theme.duo.color.green};
`;

type Props = {};

const Verify: React.FC<Props> = () => {
  const { viewer, retry } = useViewerQuery();
  const seeHint = useSeeHint();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sent, setSent] = React.useState(false);
  const verified = React.useRef(false);
  const isIdle = useIdle(2000);
  const token = useGetState("token");

  const [resend] = useMutation<VerifyMutation>(ResendQL);
  const [verifyUser] = useMutation<VerifyingMutation>(VerifyUserQL);

  const [searchParams] = useSearchParams();
  const verifying = searchParams.get(VERIFY.verifyParam);
  const verifyHash = searchParams.get(VERIFY.verifyParam);

  React.useEffect(() => {
    if (!isIdle && token) retry(); // re-check viewer's emailVerified when comes back from email, but only if user is logged in
  }, [isIdle, retry, token]);

  const close = React.useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  React.useEffect(() => {
    seeHint(VERIFYHINT);
  }, [seeHint]);

  React.useEffect(() => {
    if (verifyHash && !verified.current) {
      verifyUser({
        variables: { hash: verifyHash },
        onCompleted: () => snackbar(t("Your email was verified"))
      });
      verified.current = true;
      isApp() && close();
    }
  }, [verifyHash, verifyUser, close, t]);

  React.useEffect(() => {
    if (viewer?.emailVerified) close();
  }, [viewer?.emailVerified, close]);

  return (
    <Wrap>
      <SIconButton onClick={close}>
        <CloseIcon />
      </SIconButton>
      <SPaper>
        <TopIco>
          <MailOutlineIcon />
        </TopIco>
        {verified.current ? (
          <DownloadButton>
            <Trans>Continue in the App</Trans>
          </DownloadButton>
        ) : verifying ? (
          <Progress>
            <CircularProgress />
          </Progress>
        ) : (
          <>
            <Title>
              <Trans>{VERIFYTRANS}</Trans>
            </Title>
            <Text>
              <Trans>Click the link in the email we've sent you</Trans>
              <br />
              {viewer?.email && (
                <>
                  <strong>{viewer.email}</strong>
                  <IconButton style={{ padding: 6 }} color="primary" onClick={() => navigate(USEREDIT.url())}>
                    <EditIcon style={{ fontSize: 20 }} />
                  </IconButton>
                </>
              )}
            </Text>
            {sent ? (
              <Green>
                <Trans>Check your email</Trans>...
              </Green>
            ) : token ? (
              <Button
                color="primary"
                onClick={() => {
                  resend({ variables: {} });
                  setSent(true);
                }}
              >
                <Trans>Resend verification link</Trans>
              </Button>
            ) : (
              // user shouldn't be able to end up on /verify url without token OR hash
              <p style={{ color: "red" }}>
                Something went wrong, printscreen this and tell us about your experience on{" "}
                <a href="mailto:info@duocards.com">info@duocards.com</a> so we can help.
              </p>
            )}
          </>
        )}
      </SPaper>
    </Wrap>
  );
};

export default Verify;
